import {
  flattenBlocks, collectTemplateRegions, splitIntoSections,
  HEADING_TAGS, type FlatBlock, type TemplateRegion,
} from "./outputAnnotations";

export type MetricStatus = "good" | "warn" | "bad";

export type FindingStatus =
  | "verbatim"
  | "paraphrased"
  | "missing"
  | "skipped"
  | "covered"
  | "partial";

export type Finding = {
  status: FindingStatus;
  templateText: string;
  outputText?: string;
  parentHeading?: string;
  score?: number;
  wordCount?: number;
};

export type ClassMetric = {
  key: string;
  name: string;
  description: string;
  total: number;
  rawScore: number;
  status: MetricStatus;
  display: string;
  findings: Finding[];
};

export type Scorecard = {
  overall: number;
  status: MetricStatus;
  classifierConfidence: number;
  metrics: ClassMetric[];
};

const VERBATIM_THRESHOLD = 0.95;
const PARTIAL_THRESHOLD = 0.4;
const FWV_SEGMENT_MIN_LEN = 8;
const VARIABLE_PARTIAL_WORD_THRESHOLD = 30;

function statusFor(score: number): MetricStatus {
  if (score >= 80) return "good";
  if (score >= 50) return "warn";
  return "bad";
}

function normalize(s: string): string {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}

function countWords(s: string): number {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}

function scoreToRegion(template: string, output: string): number {
  if (!template || !output) return 0;
  if (output === template) return 1.0;
  if (output.includes(template)) return template.length / Math.max(1, output.length);
  if (template.includes(output)) return output.length / Math.max(1, template.length);
  const words = template.split(/\s+/).filter((w) => w.length >= 4);
  if (words.length === 0) return 0;
  let hits = 0;
  for (const w of words) if (output.includes(w)) hits++;
  const wordScore = (hits / words.length) * 0.6;
  return wordScore > 0.5 ? wordScore : 0;
}

function findBestForRegion(regionText: string, blocks: FlatBlock[]): { score: number; outputText: string | null } {
  let best = 0;
  let bestText: string | null = null;
  for (const b of blocks) {
    if (b.tag === "BLOCKQUOTE" || HEADING_TAGS.has(b.tag) || b.text.length === 0) continue;
    const score = scoreToRegion(regionText, b.text);
    if (score > best) {
      best = score;
      bestText = b.text;
      if (best === 1.0) return { score: 1.0, outputText: bestText };
    }
  }
  return { score: best, outputText: bestText };
}

// --- Metric 1: FIXED fidelity ----------------------------------------------

function computeFixedFidelity(
  templateRegions: TemplateRegion[],
  outputBlocks: FlatBlock[],
): ClassMetric {
  const findings: Finding[] = [];
  for (const region of templateRegions) {
    if (region.type !== "fixed") continue;
    if (!region.fixedText) continue;
    const { score, outputText } = findBestForRegion(region.fixedText, outputBlocks);
    let status: FindingStatus;
    if (score >= VERBATIM_THRESHOLD) status = "verbatim";
    else if (score >= PARTIAL_THRESHOLD) status = "paraphrased";
    else status = "missing";
    findings.push({
      status,
      templateText: region.fixedText,
      outputText: status === "paraphrased" ? outputText ?? undefined : undefined,
      score,
    });
  }
  const total = findings.length;
  const verbatim = findings.filter((f) => f.status === "verbatim").length;
  const paraphrased = findings.filter((f) => f.status === "paraphrased").length;
  const missing = findings.filter((f) => f.status === "missing").length;
  const rate = total > 0 ? verbatim / total : 1;
  const raw = rate * 100;
  return {
    key: "fixed-fidelity",
    name: "Fixed fidelity",
    description:
      "Of all FIXED regions in the template (regulatory text, firm boilerplate, disclaimers), how many appear verbatim in the output. Paraphrased FIXED regions are CRITICAL deviations.",
    total,
    rawScore: raw,
    status: statusFor(raw),
    display: `${verbatim}/${total} verbatim${paraphrased ? ` · ${paraphrased} paraphrased` : ""}${missing ? ` · ${missing} missing` : ""}`,
    findings,
  };
}

// --- Metric 2: FIXED-CONDITIONAL handling ----------------------------------

function computeConditionalHandling(
  templateRegions: TemplateRegion[],
  outputBlocks: FlatBlock[],
): ClassMetric {
  const findings: Finding[] = [];
  for (const region of templateRegions) {
    if (region.type !== "fixed-conditional") continue;
    if (!region.fixedText) continue;
    const { score, outputText } = findBestForRegion(region.fixedText, outputBlocks);
    let status: FindingStatus;
    if (score >= VERBATIM_THRESHOLD) status = "verbatim";
    else if (score >= PARTIAL_THRESHOLD) status = "paraphrased";
    else status = "skipped";
    findings.push({
      status,
      templateText: region.fixedText,
      outputText: status === "paraphrased" ? outputText ?? undefined : undefined,
      score,
    });
  }
  const total = findings.length;
  const verbatim = findings.filter((f) => f.status === "verbatim").length;
  const paraphrased = findings.filter((f) => f.status === "paraphrased").length;
  const skipped = findings.filter((f) => f.status === "skipped").length;
  const rate = total > 0 ? (verbatim + skipped) / total : 1;
  const raw = rate * 100;
  return {
    key: "conditional-handling",
    name: "Conditional handling",
    description:
      "Of all FIXED-CONDITIONAL regions, how many are either included verbatim OR cleanly skipped (the condition didn't apply). Skipping is fine; paraphrasing when included is the failure mode.",
    total,
    rawScore: raw,
    status: statusFor(raw),
    display: `${verbatim} kept · ${skipped} skipped${paraphrased ? ` · ${paraphrased} paraphrased` : ""}`,
    findings,
  };
}

// --- Metric 3: FIXED-WITH-VARIABLES boilerplate preservation ---------------

function fwvSegments(fixedText: string): string[] {
  return fixedText
    .split(/\s+/)
    .reduce<string[]>((acc, w) => {
      if (acc.length === 0) return [w];
      const last = acc[acc.length - 1]!;
      if (last.length < 80) {
        acc[acc.length - 1] = `${last} ${w}`;
      } else {
        acc.push(w);
      }
      return acc;
    }, [])
    .filter((seg) => seg.length >= FWV_SEGMENT_MIN_LEN);
}

function computeFwvBoilerplate(
  templateRegions: TemplateRegion[],
  outputBlocks: FlatBlock[],
): ClassMetric {
  const haystack = outputBlocks
    .filter((b) => !HEADING_TAGS.has(b.tag) && b.tag !== "BLOCKQUOTE")
    .map((b) => b.text)
    .join(" \n ");
  const findings: Finding[] = [];
  for (const region of templateRegions) {
    if (region.type !== "fixed-variable") continue;
    if (!region.fixedText) continue;
    const segments = fwvSegments(region.fixedText);
    if (segments.length === 0) continue;
    const hits = segments.filter((s) => haystack.includes(s)).length;
    const rate = hits / segments.length;
    let status: FindingStatus;
    if (rate >= 0.9) status = "verbatim";
    else if (rate >= 0.4) status = "paraphrased";
    else status = "missing";
    findings.push({
      status,
      templateText: region.fixedText,
      score: rate,
    });
  }
  const total = findings.length;
  const preserved = findings.filter((f) => f.status === "verbatim").length;
  const paraphrased = findings.filter((f) => f.status === "paraphrased").length;
  const missing = findings.filter((f) => f.status === "missing").length;
  const rate = total > 0 ? preserved / total : 1;
  const raw = rate * 100;
  return {
    key: "fwv-boilerplate",
    name: "Fixed-with-variables boilerplate",
    description:
      "Of all FIXED-WITH-VARIABLES regions, how many have their non-slot boilerplate preserved verbatim in the output. The variable slot values aren't checked here, only the fixed wording around them.",
    total,
    rawScore: raw,
    status: statusFor(raw),
    display: `${preserved}/${total} preserved${paraphrased ? ` · ${paraphrased} paraphrased` : ""}${missing ? ` · ${missing} missing` : ""}`,
    findings,
  };
}

// --- Metric 4: VARIABLE section coverage -----------------------------------

function parentHeadingFor(blockIdx: number, blocks: FlatBlock[]): string {
  for (let i = blockIdx; i >= 0; i--) {
    const b = blocks[i];
    if (b && HEADING_TAGS.has(b.tag) && b.text.length > 0) return b.text;
  }
  return "";
}

function computeVariableCoverage(
  templateRegions: TemplateRegion[],
  templateBlocks: FlatBlock[],
  outputBlocks: FlatBlock[],
): ClassMetric {
  const headings = new Map<string, number>();
  for (const region of templateRegions) {
    if (region.type !== "variable") continue;
    const idx = region.blockIndices[0];
    if (idx === undefined) continue;
    const parent = parentHeadingFor(idx, templateBlocks);
    if (!parent) continue;
    headings.set(parent, (headings.get(parent) ?? 0) + 1);
  }

  const findings: Finding[] = [];
  const outputSections = splitIntoSections(outputBlocks);
  const outputByHeading = new Map<string, FlatBlock[]>();
  for (const s of outputSections) {
    if (s.heading) outputByHeading.set(s.heading, s.blocks);
  }

  for (const [heading, instructionCount] of headings) {
    const sectionBlocks = outputByHeading.get(heading);
    if (!sectionBlocks) {
      findings.push({ status: "missing", templateText: heading, parentHeading: heading });
      continue;
    }
    const wordCount = sectionBlocks
      .filter((b) => !HEADING_TAGS.has(b.tag) && b.tag !== "BLOCKQUOTE")
      .map((b) => countWords(b.text))
      .reduce((a, b) => a + b, 0);
    const status: FindingStatus =
      wordCount >= VARIABLE_PARTIAL_WORD_THRESHOLD ? "covered" : "partial";
    findings.push({
      status,
      templateText: heading,
      parentHeading: heading,
      wordCount,
      score: instructionCount,
    });
  }

  const total = findings.length;
  const covered = findings.filter((f) => f.status === "covered").length;
  const partial = findings.filter((f) => f.status === "partial").length;
  const missing = findings.filter((f) => f.status === "missing").length;
  const rate = total > 0 ? (covered + 0.5 * partial) / total : 1;
  const raw = rate * 100;
  return {
    key: "variable-coverage",
    name: "Variable coverage",
    description:
      "Of all parent headings that host VARIABLE blockquote instructions in the template, how many have a corresponding heading in the output with reasonable content underneath. Missing = heading absent. Partial = heading present but <30 words underneath.",
    total,
    rawScore: raw,
    status: statusFor(raw),
    display: `${covered} covered · ${partial} thin · ${missing} missing`,
    findings,
  };
}

// --- Classifier confidence -------------------------------------------------

function computeClassifierConfidence(
  templateRegions: TemplateRegion[],
  outputBlocks: FlatBlock[],
): number {
  let total = 0;
  let matched = 0;
  for (const b of outputBlocks) {
    if (b.tag === "BLOCKQUOTE" || HEADING_TAGS.has(b.tag) || b.text.length === 0) continue;
    total++;
    let best = 0;
    for (const r of templateRegions) {
      if (!r.fixedText) continue;
      const s = scoreToRegion(r.fixedText, b.text);
      if (s > best) best = s;
      if (best >= PARTIAL_THRESHOLD) break;
    }
    if (best >= PARTIAL_THRESHOLD) matched++;
  }
  return total > 0 ? (matched / total) * 100 : 100;
}

// --- Entry point -----------------------------------------------------------

export function computeScorecard(templateHtml: string, outputHtml: string): Scorecard {
  const { blocks: templateBlocks } = flattenBlocks(templateHtml);
  const { blocks: outputBlocks } = flattenBlocks(outputHtml);
  const regions = collectTemplateRegions(templateBlocks);

  const metrics: ClassMetric[] = [
    computeFixedFidelity(regions, outputBlocks),
    computeConditionalHandling(regions, outputBlocks),
    computeFwvBoilerplate(regions, outputBlocks),
    computeVariableCoverage(regions, templateBlocks, outputBlocks),
  ];

  const scoredMetrics = metrics.filter((m) => m.total > 0);
  const overall = scoredMetrics.length > 0
    ? scoredMetrics.reduce((s, m) => s + m.rawScore, 0) / scoredMetrics.length
    : 0;
  const confidence = computeClassifierConfidence(regions, outputBlocks);

  return {
    overall,
    status: statusFor(overall),
    classifierConfidence: confidence,
    metrics,
  };
}
